<template>
  <v-container fluid>
    <h1 class="headline mt-2 mb-2">{{ $t('user_management') }}</h1>

    <v-layout v-if="!isAgent" wrap>
      <v-flex xs12 :sm6="selectedGroup" class="pa-1">
        <v-layout class="mb-2">
          <v-flex>
            <h2 class="title grey--text mt-2">Group</h2>
          </v-flex>
          <add-admin-group :admins="admins" @addGroup="addGroup" @onRefresh="handleFetchGroup" />
        </v-layout>

        <template v-if="groups && groups.length > 0">
          <v-card>
            <template v-for="(group, gindex) in groups">
              <div :key="`group_${group.id}`" class="pa-3">
                <v-layout>
                  <v-flex>
                    <div class="font-weight-bold">
                      {{ group.groupname }}
                    </div>
                    <div class="caption">
                      {{ group.groupdescription ? group.groupdescription : '&nbsp;' }}
                    </div>
                  </v-flex>
                  <edit-admin-group
                    :key="'group_' + group.id + '_' + gindex"
                    v-model="groups[gindex]"
                    :admins="admins"
                    @editGroup="editGroup"
                    @onRefresh="handleFetchGroup"
                  ></edit-admin-group>

                  <v-btn
                    fab
                    small
                    color="primary"
                    outline
                    class="mx-1 my-0"
                    @click="onDeleteGroup(group)"
                  >
                    <v-icon>delete</v-icon>
                  </v-btn>

                  <v-icon class="mt-2 ml-2" style="cursor: pointer" @click="selectedGroup = group">
                    arrow_forward_ios
                  </v-icon>
                </v-layout>
              </div>
              <v-divider v-if="groups.length - 1 != gindex" :key="`group_divider_${group.id}`" />
            </template>
          </v-card>
        </template>
        <div v-else class="text-xs-center grey lighten-3 py-3" style="border-radius: 10px">
          No data available
        </div>
      </v-flex>
      <v-flex v-if="selectedGroup" xs12 :sm6="selectedGroup" class="pa-1">
        <v-layout class="mb-2">
          <v-flex>
            <h2 class="title grey--text mt-2">{{ selectedGroup.groupname }} Group</h2>
          </v-flex>
          <add-admin-sub-group
            :group="selectedGroup"
            @addSubGroup="addSubGroup"
            @onRefresh="handleFetchSubGroup"
          ></add-admin-sub-group>
        </v-layout>

        <template v-if="filterSubGroups && filterSubGroups.length > 0">
          <v-card>
            <template v-for="(subgroup, gindex) in filterSubGroups">
              <div :key="`group_${subgroup.id}`" class="pa-3">
                <v-layout>
                  <v-flex>
                    <div class="font-weight-bold">
                      {{ subgroup.subgroupname }}
                    </div>
                    <div class="caption">
                      {{ subgroup.subgroupdescription ? subgroup.subgroupdescription : '&nbsp;' }}
                    </div>
                  </v-flex>
                  <edit-admin-sub-group
                    v-model="filterSubGroups[gindex]"
                    :group="selectedGroup"
                    @editSubGroup="editSubGroup"
                    @onRefresh="handleFetchSubGroup"
                  ></edit-admin-sub-group>

                  <v-btn
                    fab
                    small
                    color="primary"
                    outline
                    class="mx-1 my-0"
                    @click="onDeleteSubGroup(subgroup)"
                    ><v-icon>delete</v-icon></v-btn
                  >
                </v-layout>
              </div>
              <v-divider
                v-if="filterSubGroups.length - 1 != gindex"
                :key="`group_divider_${subgroup.id}`"
              />
            </template>
          </v-card>
        </template>
        <div v-else class="text-xs-center grey lighten-3 py-3" style="border-radius: 10px">
          No data available
        </div>
      </v-flex>
    </v-layout>

    <div v-if="showWorkingHours" class="pa-1 mt-4">
      <div>
        <WorkingHours />
      </div>
    </div>

    <div class="pa-1 mt-4">
      <v-layout class="mb-2">
        <v-flex>
          <h2 class="title grey--text mt-2">Admin Users</h2>
        </v-flex>
        <exportxlsx v-if="isZwizAdmin()" v-model="exportAdminData" :small="true"></exportxlsx>
        <add-user
          v-if="!isAgent && isZwizAdmin()"
          @addUser="onAddUser"
          @onRefresh="handleFetchAdmin"
        ></add-user>
        <v-btn
          v-if="isZwizAdmin() || isSuperAdmin"
          small
          depressed
          color="primary"
          @click="isLogoutDialogVisible = true"
          >Check out</v-btn
        >
      </v-layout>

      <v-data-table
        :headers="headers"
        :pagination.sync="pagination"
        :items="filterAdmins"
        class="elevation-1"
        hide-actions
      >
        <template #items="props">
          <UserItem
            :key="props.item.adminid"
            :user="props"
            :admins="admins"
            :is-agent="isAgent"
            :is-super-admin="isSuperAdmin"
            @onDelete="onDeleteUser"
            @assignStatus="
              (adminid, status) => {
                putassignstatusByadminid(adminid, status)
                props.item.assign_status = !props.item.assign_status
              }
            "
          >
            <template #action>
              <template v-if="!loadingAgentGroup && !loadingAgentSubGroup">
                <edit-user
                  v-if="!isAgent"
                  :key="props.item.adminid"
                  v-model="props.item"
                  @editUser="onEditUser"
                  @onRefresh="handleFetchAdmin"
                ></edit-user>
              </template>
            </template>
          </UserItem>
        </template>
      </v-data-table>
    </div>

    <LogoutDialog
      v-if="isLogoutDialogVisible"
      :users="admins"
      @close="isLogoutDialogVisible = false"
      @success="handleLogoutDialogSuccess"
    />
  </v-container>
</template>

<script>
import api from '@/api'
import { mapGetters } from 'vuex'
const AddAdminGroup = () => import('./AddAdminGroup')
const EditAdminGroup = () => import('./EditAdminGroup')
const AddAdminSubGroup = () => import('./AddAdminSubGroup')
const EditAdminSubGroup = () => import('./EditAdminSubGroup')

const AddUser = () => import('./AddUser')
const EditUser = () => import('./EditUser')
import WorkingHours from './WorkingHours'
import exportxlsx from '@/components/elements/exportxlsx.vue'
import ticket3AdminManagementApi from '@/api/ticket3/admin-management'
import { handleApiError } from '@/api/util'
import UserItem from './UserItem'
import { PAGE } from '@/constants/page'
// import { ToggleButton } from 'vue-js-toggle-button'
import LogoutDialog from './LogoutDialog'

export default {
  name: 'UserManagement',
  components: {
    AddAdminGroup,
    EditAdminGroup,
    AddAdminSubGroup,
    EditAdminSubGroup,

    AddUser,
    EditUser,
    // ToggleButton,
    WorkingHours,
    exportxlsx,
    UserItem,
    LogoutDialog
  },
  data() {
    return {
      loading: false,
      adminloading: [],
      admins: [],
      groups: [],
      subgroups: [],
      pagination: {
        rowsPerPage: 999
      },
      selectedGroup: null,
      currentUser: null,
      isLogoutDialogVisible: false
    }
  },
  computed: {
    ...mapGetters({
      selectedBot: 'selectedBot',
      loadingAgentGroup: 'getLoadingAgentGroup',
      loadingAgentSubGroup: 'getLoadingAgentSubgroup'
    }),
    filterSubGroups() {
      if (this.selectedGroup) {
        return this.subgroups.filter((el) => {
          return el.admingroupid == this.selectedGroup.id
        })
      }
      return []
    },
    showWorkingHours() {
      return !this.isAgent
    },
    isAgent() {
      return this.$store.state.privilege && this.$store.state.privilege.role === 'agent'
    },
    isSuperAdmin() {
      return (
        this.$store.state.privilege &&
        ['all', 'superadmin'].includes(this.$store.state.privilege.role)
      )
    },
    exportAdminData() {
      const baseData = this.filterAdmins
      let output = []
      baseData.forEach((adminData) => {
        let privilege
        try {
          privilege = JSON.parse(adminData.privilege)
        } catch (error) {
          privilege = null
        }
        output.push({
          'Admin ID': adminData.adminid,
          Type: adminData.type,
          'Group Name': adminData.groupname,
          'Subgroup Name': adminData.subgroupname,
          Role: privilege && privilege.role ? privilege.role : '-'
        })
      })
      return output
    },
    filterAdmins() {
      let admins = []
      if (this.isAgent) {
        return this.admins.filter((item) => item.adminid === this.$store.state.userid)
      } else {
        admins = this.admins
      }
      return admins
    },
    headers() {
      if (this.isZwizAdmin() || this.isSuperAdmin) {
        return [
          { text: 'No.', value: 'index', sortable: false },
          { text: 'Username', sortable: false },
          { text: 'Password', sortable: false },
          { text: 'Role', sortable: false },
          { text: 'Group', sortable: false },
          { text: 'Sub Group', sortable: false },
          { text: 'Action', sortable: false },
          { text: 'Assigned', sortable: false },
          { text: 'Checked in', sortable: false }
        ]
      }

      return [
        { text: 'No.', value: 'index', sortable: false },
        { text: 'Username', sortable: false },
        { text: 'Password', sortable: false },
        { text: 'Role', sortable: false },
        { text: 'Group', sortable: false },
        { text: 'Sub Group', sortable: false }
      ]
    }
  },

  async mounted() {
    this.$store.dispatch('loading')
    this.loading = true
    try {
      await this.handleFetchGroup()
      await this.handleFetchSubGroup()
      await this.handleFetchAdmin()
    } catch (error) {
      console.error(error)
    }
    this.loading = false
    this.$store.dispatch('loading')
  },
  methods: {
    async handleFetchAdmin() {
      try {
        const admins = await this.getAdmins()
        this.admins = admins
        return true
      } catch (error) {
        handleApiError(error)
      }
    },
    async handleFetchGroup() {
      try {
        const responseGroup = await this.getAdminGroup()
        if (responseGroup.status == 200) {
          this.groups = responseGroup.data && responseGroup.data.data ? responseGroup.data.data : []
          this.$store.state.agentGroups = this.groups
        }
        return true
      } catch (error) {
        handleApiError(error)
      }
    },
    async handleFetchSubGroup() {
      try {
        const responseSubGroup = await this.getAdminSubGroup()
        if (responseSubGroup.status == 200) {
          this.subgroups = responseSubGroup.data.data
          this.$store.state.agentSubgroups = this.subgroups
        }
        return true
      } catch (error) {
        console.error(error)
      }
    },
    putassignstatusByadminid(adminid, status) {
      const vm = this
      var data = {
        pageid: this.selectedBot.pageid,
        assign_status: !status,
        adminid: adminid
      }
      this.adminloading.push(adminid)
      api.putassignstatusByadminid(data, (err, response) => {
        if (response.responsecode == 200) {
          var index = vm.adminloading.findIndex((el) => el == adminid)
          vm.adminloading.splice(index, 1)
          status = !status
        }
      })
    },
    async onAddUser() {
      this.$store.dispatch('loadAgents')
      this.handleFetchAdmin()
    },
    async onEditUser() {
      this.$store.dispatch('loadAgents')
      this.handleFetchAdmin()
    },
    async getAdmins() {
      const params = {
        page_id: this.selectedBot.pageid,
        limit: 100
      }
      const response = await ticket3AdminManagementApi.getAdminUser({ params })
      if (response.status == 200) {
        const allAdmins = response.data.data
        const admins = this.isZwizAdmin()
          ? allAdmins
          : allAdmins.filter((admin) => {
              return !admin.adminid.includes('zwiz')
            })
        return admins || []
      }
    },
    getAdminGroup() {
      const params = {
        page_id: this.selectedBot.pageid
      }
      return ticket3AdminManagementApi.getAdminGroup({ params })
    },
    getAdminSubGroup() {
      const params = {
        page_id: this.selectedBot.pageid
      }
      return ticket3AdminManagementApi.getAdminSubGroup({ params })
    },
    addGroup(payload) {
      this.groups.push(payload)
      this.$store.dispatch('loadAgentGroups')
    },
    editGroup(payload) {
      const index = this.groups.findIndex((el) => {
        return el.id == payload.id
      })
      if (index != -1) {
        this.$set(this.groups, index, payload)
      }
      this.$store.dispatch('loadAgentGroups')
    },
    async onDeleteGroup(group) {
      const result = await this.$swal.fire({
        title: this.$t('Do you want to delete {name}?', {
          name: this.noSpaceAndSpecial(group.groupname)
        }),
        type: 'warning',
        confirmButtonText: this.$t('Confirm'),
        cancelButtonText: this.$t('cancel'),
        reverseButtons: true,
        showCancelButton: true
      })
      if (result.value) {
        try {
          await this.deleteGroup(group)
          const index = this.groups.findIndex((el) => {
            return el.id == group.id
          })
          if (this.selectedGroup && this.selectedGroup == group) {
            this.selectedGroup = null
          }
          if (index != -1) {
            this.groups.splice(index, 1)
            this.$store.dispatch('loadAgentGroups')
          }
        } catch (error) {
          handleApiError(error)
        }
      }
    },
    deleteGroup(data) {
      const request = {
        data: { id: data.id, page_id: this.selectedBot.pageid }
      }
      return ticket3AdminManagementApi.deleteAdminGroup(request)
    },
    addSubGroup(payload) {
      this.subgroups.push(payload)
      this.$store.dispatch('loadAgentSubGroups')
    },
    editSubGroup(payload) {
      const index = this.subgroups.findIndex((el) => {
        return el.id == payload.id
      })
      if (index != -1) {
        this.$set(this.subgroups, index, payload)
      }
      this.$store.dispatch('loadAgentSubGroups')
    },
    async onDeleteSubGroup(group) {
      const result = await this.$swal.fire({
        title: this.$t('Do you want to delete {name}?', {
          name: this.noSpaceAndSpecial(group.subgroupname)
        }),
        type: 'warning',
        confirmButtonText: this.$t('Confirm'),
        cancelButtonText: this.$t('cancel'),
        reverseButtons: true,
        showCancelButton: true
      })
      if (result.value) {
        try {
          await this.deleteSubGroup(group)
          var index = this.subgroups.findIndex((el) => {
            return el.id == group.id
          })
          if (index != -1) {
            this.subgroups.splice(index, 1)
            this.$store.dispatch('loadAgentSubGroups')
          }
        } catch (error) {
          handleApiError(error)
        }
      }
    },
    deleteSubGroup(data) {
      const request = {
        data: { id: data.id, page_id: this.selectedBot.pageid, admingroupid: data.admingroupid }
      }
      return ticket3AdminManagementApi.deleteAdminSubGroup(request)
    },
    async onDeleteUser(user) {
      const result = await this.$swal.fire({
        title: this.$t('Do you want to delete {name}?', {
          name: this.noSpaceAndSpecial(user.adminid)
        }),
        type: 'warning',
        confirmButtonText: this.$t('Confirm'),
        cancelButtonText: this.$t('cancel'),
        reverseButtons: true,
        showCancelButton: true
      })
      if (result.value) {
        this.$store.dispatch('loading')
        try {
          await this.deleteadminusers({ userid: user.adminid })
          if (this.$store.state.userid == user.adminid) {
            this.$store.dispatch('logout', null).then()
            if (this.user.type == 'admin') {
              this.$router.push('/' + this.currentLang + '/nigol')
            } else {
              this.$router.push('/' + this.currentLang + '/login')
            }
          }
          await this.handleFetchAdmin()
          this.$store.dispatch('loading')
        } catch (error) {
          handleApiError(error)
        }
      }
    },
    deleteadminusers(data) {
      var request = {
        data: { adminid: data.userid, page_id: this.selectedBot.pageid }
      }
      return ticket3AdminManagementApi.deleteAdminUser(request)
    },
    handleLogoutDialogSuccess() {
      this.handleFetchAdmin()
    }
  }
}
</script>

<style lang="scss" scoped></style>



// WEBPACK FOOTER //
// src/components/view/ticket3/agent-management/user/Users.vue